import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, MessageCircle, HelpCircle, ArrowLeft, Smile, Users, ChevronRight, Map, CheckCircle2, Plus, Bot, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const QUICK_EMOJIS = ['👋', '😊', '👍', '❤️', '🔧', '📱', '🎉', '💯'];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const messagesChanged = (prev, next) => {
  if (!prev && !next) return false;
  if (!prev || !next) return true;
  if (prev.length !== next.length) return true;
  if (prev.length === 0) return false;
  return prev[prev.length - 1]?.id !== next[next.length - 1]?.id;
};

// MSN-style notification sound using Web Audio API
const playNotifSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [800, 1000, 900, 1100];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.1);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.1);
    });
  } catch {}
};

const getCompanyInfo = () => {
  const host = window.location.hostname;
  if (host.includes('guyarepair')) return { name: 'Guyarepair', logo: '/guyarepair_logo.png' };
  return { name: 'R.E.Mobiles', logo: '/logo.png' };
};

const ClientChatWidget = ({ api, user, b2b = false }) => {
  const { t, formatDate } = useLanguage();
  // API paths differ for B2B vs standard client
  const convoListPath = b2b ? '/b2b/chat/conversations' : '/chat/client/conversations';
  const msgBasePath = b2b ? '/b2b/chat' : '/chat';
  const startConvoPath = b2b ? '/b2b/chat/conversations' : '/chat/client/start';
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('staff'); // 'staff' | 'aide'
  const [pulse, setPulse] = useState(true);
  const company = getCompanyInfo();

  // === STAFF CHAT STATE ===
  const [conversations, setConversations] = useState([]);
  const [convosLoaded, setConvosLoaded] = useState(false);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [staffMessages, setStaffMessages] = useState([]);
  const [staffInput, setStaffInput] = useState('');
  const [staffSending, setStaffSending] = useState(false);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [staffUnread, setStaffUnread] = useState(0);
  const [startingConvo, setStartingConvo] = useState(false);
  const staffEndRef = useRef(null);
  const staffInputRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const prevMsgCountRef = useRef(0);
  const startingConvoRef = useRef(false);
  const staffMessagesRef = useRef([]);

  // === AI CHAT STATE ===
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiView, setAiView] = useState('home'); // 'home' | 'chat' | 'tour'
  const [faq, setFaq] = useState([]);
  const [tours, setTours] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [tourStep, setTourStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const aiEndRef = useRef(null);

  // === STAFF ONLINE STATUS ===
  const [staffOnline, setStaffOnline] = useState(false);
  const [autoReplyActive, setAutoReplyActive] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 5000);
    return () => clearTimeout(t);
  }, []);

  // Check staff online status + business hours
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/chat/business-hours`);
        const data = await res.json();
        setStaffOnline(data.staff_online || false);
        setAutoReplyActive(data.auto_reply_active || false);
        setBusinessHours(data.hours || null);
      } catch {}
    };
    checkStatus();
    const iv = setInterval(checkStatus, 30000);
    return () => clearInterval(iv);
  }, []);

  // Load FAQ and tours
  useEffect(() => {
    fetch(`${API_URL}/api/guide/faq`).then(r => r.json()).then(setFaq).catch(() => {});
    fetch(`${API_URL}/api/guide/tours?audience=client`).then(r => r.json()).then(setTours).catch(() => {});
  }, []);

  const applyMessages = useCallback((incoming) => {
    const next = incoming || [];
    if (messagesChanged(staffMessagesRef.current, next)) {
      staffMessagesRef.current = next;
      setStaffMessages(next);
    }
  }, []);

  // === STAFF CHAT LOGIC ===
  const fetchConvos = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.get(convoListPath);
      setConversations(res.data || []);
      const newUnread = (res.data || []).reduce((s, c) => s + (c.unread_count || 0), 0);
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        playNotifSound();
      }
      prevUnreadRef.current = newUnread;
      setStaffUnread(newUnread);
    } catch {}
    finally {
      setConvosLoaded(true);
    }
  }, [api, convoListPath]);

  const startNewConvo = useCallback(async () => {
    if (!api || startingConvoRef.current) return null;
    startingConvoRef.current = true;
    setStartingConvo(true);
    try {
      const res = await api.post(
        startConvoPath,
        b2b ? { related_type: 'b2b_general', subject: t('chatWidget.newConversationSubject') } : { subject: t('chatWidget.newConversationSubject') }
      );
      if (res.data?.id) {
        setSelectedConvo(res.data);
        staffMessagesRef.current = [];
        setStaffMessages([]);
        prevMsgCountRef.current = 0;
        fetchConvos();
        return res.data;
      }
    } catch (err) {
      console.error('Error creating conversation:', err);
    } finally {
      startingConvoRef.current = false;
      setStartingConvo(false);
    }
    return null;
  }, [api, b2b, startConvoPath, fetchConvos]);

  const loadStaffMessages = useCallback(async (convoId, markRead = true) => {
    if (!api || !convoId) return [];
    try {
      const res = await api.get(`${msgBasePath}/conversations/${convoId}/messages`);
      const incoming = res.data || [];
      applyMessages(incoming);
      if (markRead) {
        api.post(`${msgBasePath}/conversations/${convoId}/mark-read`).catch(() => {});
      }
      return incoming;
    } catch {
      return [];
    }
  }, [api, msgBasePath, applyMessages]);

  useEffect(() => {
    if (api && user) fetchConvos();
    const iv = setInterval(() => { if (api && user) fetchConvos(); }, 10000);
    return () => clearInterval(iv);
  }, [api, user, fetchConvos]);

  // Auto-select or create conversation once list is loaded (guarded against duplicates)
  useEffect(() => {
    if (!isOpen || activeTab !== 'staff' || selectedConvo || !convosLoaded || !api) return;
    if (conversations.length === 1) {
      setSelectedConvo(conversations[0]);
    } else if (conversations.length === 0 && !startingConvoRef.current) {
      startNewConvo();
    }
  }, [isOpen, activeTab, conversations, selectedConvo, api, convosLoaded, startNewConvo]);

  useEffect(() => {
    if (!selectedConvo?.id || !api) return;
    prevMsgCountRef.current = 0;
    loadStaffMessages(selectedConvo.id);
    const iv = setInterval(() => loadStaffMessages(selectedConvo.id), 8000);
    return () => clearInterval(iv);
  }, [selectedConvo?.id, api, loadStaffMessages]);

  useEffect(() => {
    if (staffMessages.length > prevMsgCountRef.current && staffEndRef.current) {
      staffEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    prevMsgCountRef.current = staffMessages.length;
  }, [staffMessages]);

  const isMyMsg = (msg) => {
    if (msg._optimistic) return true;
    if (msg.sender_id && msg.sender_id === user?.id) return true;
    if (user?.role === 'client' && msg.sender_role === 'client') return true;
    return msg.sender_name === user?.username;
  };

  const sendStaffMsg = async () => {
    if (!staffInput.trim() || staffSending) return;
    let convo = selectedConvo;
    if (!convo?.id) {
      convo = await startNewConvo();
      if (!convo?.id) return;
    }

    const txt = staffInput.trim();
    setStaffInput('');
    setStaffSending(true);
    setAwaitingReply(true);

    const optimistic = {
      id: `tmp-${Date.now()}`,
      sender_id: user?.id,
      sender_role: 'client',
      sender_name: user?.username || t('chatWidget.me'),
      content: txt,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    const withOptimistic = [...staffMessagesRef.current, optimistic];
    staffMessagesRef.current = withOptimistic;
    setStaffMessages(withOptimistic);

    try {
      await api.post(`${msgBasePath}/conversations/${convo.id}/messages`, { content: txt });
      const baseCount = withOptimistic.length;
      for (let i = 0; i < 8; i++) {
        await sleep(i === 0 ? 500 : 700);
        const res = await api.get(`${msgBasePath}/conversations/${convo.id}/messages`);
        const msgs = res.data || [];
        applyMessages(msgs);
        const hasAi = msgs.some((m) => m.sender_role === 'ai' || m.is_ai_reply);
        if (msgs.length > baseCount || hasAi) break;
      }
      fetchConvos();
    } catch (err) {
      applyMessages(staffMessagesRef.current.filter((m) => m.id !== optimistic.id));
      setStaffInput(txt);
      console.error('Send message error:', err);
    }
    setStaffSending(false);
    setAwaitingReply(false);
    staffInputRef.current?.focus();
  };

  // === AI CHAT LOGIC ===
  useEffect(() => {
    if (aiEndRef.current) aiEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  const sendAiMessage = async (text) => {
    if (!text?.trim()) return;
    const userMsg = { role: 'user', content: text.trim() };
    setAiMessages(prev => [...prev, userMsg]);
    setAiInput('');
    setAiView('chat');
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/guide/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: text.trim(), history: [...aiMessages, userMsg].slice(-8), session_id: sessionId }),
      });
      const data = await res.json();
      setAiMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
      if (data.session_id) setSessionId(data.session_id);
    } catch {
      setAiMessages(prev => [...prev, { role: 'assistant', content: t('chatWidget.aiError') }]);
    }
    setAiLoading(false);
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      return formatDate(iso, { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const statusLabel = staffOnline
    ? t('chatWidget.statusStaffOnline')
    : autoReplyActive
      ? t('chatWidget.statusAiActive')
      : t('chatWidget.statusOffline');

  if (!user || !api) return null;

  // === CLOSED STATE ===
  if (!isOpen) {
    return (
      <div className="fixed bottom-5 right-5 z-[9000] flex flex-col items-center gap-1.5">
        {pulse && (
          <div className="px-3 py-1.5 rounded-full text-[11px] font-bold text-white animate-bounce shadow-lg whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
            {t('chatWidget.launcherLabel')}
          </div>
        )}
        <button
          onClick={() => { setIsOpen(true); setPulse(false); }}
          className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 relative"
          style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', boxShadow: '0 4px 20px rgba(99,102,241,0.4)' }}
          data-testid="client-chat-widget-btn"
        >
          <MessageCircle className="w-6 h-6 text-white" />
          {staffUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1 animate-pulse">
              {staffUnread}
            </span>
          )}
        </button>
      </div>
    );
  }

  // === OPEN STATE ===
  return (
    <div className="fixed bottom-5 right-5 z-[9000] w-[360px] sm:w-[400px] flex flex-col bg-[#0e0e16] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
      style={{ maxHeight: 'min(600px, 80dvh)', height: 'min(600px, 80dvh)' }}
      data-testid="client-chat-widget-panel"
    >
      {/* Header */}
      <div className="shrink-0 px-4 py-2.5 border-b border-white/10" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1e1b4b 100%)' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <img src={company.logo} alt="" className="w-6 h-6 object-contain rounded bg-white/10 p-0.5" />
            <div>
              <span className="text-sm font-bold text-white">{company.name}</span>
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${staffOnline ? 'bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.6)]' : autoReplyActive ? 'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]' : 'bg-gray-500'}`} />
                <span className={`text-[10px] ${staffOnline ? 'text-green-400' : autoReplyActive ? 'text-amber-400' : 'text-gray-500'}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20" data-testid="client-chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-xl p-0.5">
          <button
            onClick={() => setActiveTab('staff')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === 'staff' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
            data-testid="tab-staff-chat"
          >
            <Users className="w-3.5 h-3.5" />
            {t('chatWidget.tabStaff')}
            {staffUnread > 0 && <span className="min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">{staffUnread}</span>}
          </button>
          <button
            onClick={() => setActiveTab('aide')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === 'aide' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
            data-testid="tab-aide-chat"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            {t('chatWidget.tabAi')}
          </button>
        </div>
      </div>

      {/* ===== STAFF CHAT TAB ===== */}
      {activeTab === 'staff' && !selectedConvo && (
        <div className="flex-1 overflow-y-auto min-h-0" data-testid="staff-convo-list">
          {/* New Conversation Button */}
          <div className="p-3 border-b border-white/10">
            <button
              onClick={startNewConvo}
              disabled={startingConvo}
              className="w-full py-2 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/30 font-semibold text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              data-testid="new-convo-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('chatWidget.newMessage')}
            </button>
          </div>

          {!convosLoaded ? (
            <div className="text-center py-10 px-4">
              <Loader2 className="w-8 h-8 text-blue-400/50 mx-auto mb-2 animate-spin" />
              <p className="text-gray-500 text-xs">{t('chatWidget.loading')}</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-10 px-4">
              <Users className="w-10 h-10 text-blue-500/30 mx-auto mb-2" />
              <p className="text-gray-500 text-xs">{t('chatWidget.noConversations')}</p>
              <p className="text-gray-600 text-[10px] mt-1">{t('chatWidget.emptyConvoHint', { action: t('chatWidget.newMessage') })}</p>
            </div>
          ) : (
            conversations.map(c => (
              <div
                key={c.id}
                onClick={() => setSelectedConvo(c)}
                className="px-4 py-3 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors flex items-center gap-3"
                data-testid={`convo-item-${c.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white truncate">{c.title || t('chatWidget.conversationFallback')}</div>
                  {c.last_message && <p className="text-[10px] text-gray-500 truncate mt-0.5">{typeof c.last_message === 'string' ? c.last_message : c.last_message.content}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {c.unread_count > 0 && (
                    <span className="min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1">
                      {c.unread_count}
                    </span>
                  )}
                  <span className="text-[9px] text-gray-600">
                    {c.updated_at ? formatDate(c.updated_at, { day: '2-digit', month: 'short' }) : ''}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'staff' && selectedConvo && (
        <>
          {/* Back to conversation list */}
          {conversations.length > 1 && (
            <div className="shrink-0 px-3 py-1.5 border-b border-white/5 flex items-center gap-2 bg-white/3">
              <button onClick={() => { setSelectedConvo(null); staffMessagesRef.current = []; setStaffMessages([]); }} className="p-1 rounded-lg hover:bg-white/10 text-gray-400" data-testid="back-to-convos">
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-[10px] text-gray-500 truncate">{selectedConvo.title || t('chatWidget.conversationFallback')}</span>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0" data-testid="staff-chat-messages">
            {autoReplyActive && !staffOnline && (
              <div className="mx-1 mb-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-2" data-testid="auto-reply-banner">
                <Bot className="w-4 h-4 text-amber-400 shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold text-amber-300">{t('chatWidget.autoReplyTitle')}</p>
                  <p className="text-[9px] text-amber-400/60">{t('chatWidget.autoReplyDesc')}</p>
                </div>
              </div>
            )}
            {staffMessages.length === 0 && !awaitingReply && (
              <div className="text-center py-10">
                <Users className="w-10 h-10 text-blue-500/30 mx-auto mb-2" />
                <p className="text-gray-500 text-xs">{t('chatWidget.staffEmptyTitle')}</p>
                <p className="text-gray-600 text-[10px] mt-1">{t('chatWidget.staffEmptyDesc')}</p>
              </div>
            )}
            {staffMessages.map((msg, i) => {
              const mine = isMyMsg(msg);
              const isNudge = msg.content?.includes('*Nudge!*');
              const isAi = msg.sender_role === 'ai' || msg.is_ai_reply;
              return (
                <div key={msg.id || i} className={`flex ${mine ? 'justify-end' : 'justify-start'} ${isNudge ? 'animate-bounce' : ''}`}>
                  {isAi && (
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mr-1.5 mt-1">
                      <Bot className="w-3.5 h-3.5 text-amber-400" />
                    </div>
                  )}
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-xs ${
                    isNudge ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : isAi ? 'bg-amber-500/10 text-amber-100 border border-amber-500/20 rounded-bl-md'
                    : mine ? 'bg-blue-600 text-white rounded-br-md'
                    : 'bg-white/8 text-gray-200 border border-white/10 rounded-bl-md'
                  }`}>
                    {isAi && <div className="text-[10px] font-semibold text-amber-400 mb-0.5 flex items-center gap-1 flex-wrap">
                      {t('chatWidget.aiAssistant')}
                      <span className="text-[8px] bg-amber-500/20 px-1 rounded">{t('chatWidget.badgeAuto')}</span>
                      {(msg.is_price_quote || msg.metadata?.is_price_quote) && (
                        <span className="text-[8px] bg-emerald-500/20 text-emerald-300 px-1 rounded">{t('chatWidget.badgeQuoteIndicative')}</span>
                      )}
                      {(msg.pending_devis || msg.metadata?.pending_devis) && (
                        <span className="text-[8px] bg-blue-500/20 text-blue-300 px-1 rounded">{t('chatWidget.badgeQuotePending')}</span>
                      )}
                    </div>}
                    {!mine && !isAi && <div className="text-[10px] font-semibold text-blue-400 mb-0.5">{msg.sender_name || t('chatWidget.supportFallback')}</div>}
                    <div className="break-words whitespace-pre-wrap">{msg.content}</div>
                    <div className={`text-[9px] mt-1 ${isAi ? 'text-amber-300/50' : mine ? 'text-blue-200/60' : 'text-gray-600'}`}>{formatTime(msg.created_at)}</div>
                  </div>
                </div>
              );
            })}
            {awaitingReply && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 text-amber-400 animate-spin" />
                  <span className="text-[10px] text-amber-300">{t('chatWidget.sending')}</span>
                </div>
              </div>
            )}
            <div ref={staffEndRef} />
          </div>

          {showEmojis && (
            <div className="px-3 py-1.5 border-t border-white/5 flex gap-1 flex-wrap bg-gray-900/80">
              {QUICK_EMOJIS.map(e => (
                <button key={e} onClick={() => { setStaffInput(prev => prev + e); setShowEmojis(false); staffInputRef.current?.focus(); }} className="p-1 hover:bg-white/10 rounded text-base">{e}</button>
              ))}
            </div>
          )}

          <div className="shrink-0 px-3 py-2 border-t border-white/10 flex items-center gap-2" style={{ background: '#0a0a1a' }}>
            <button onClick={() => setShowEmojis(!showEmojis)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500">
              <Smile className="w-4 h-4" />
            </button>
            <input
              ref={staffInputRef}
              type="text"
              value={staffInput}
              onChange={e => setStaffInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendStaffMsg()}
              placeholder={t('chatWidget.staffInputPlaceholder')}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/50"
              data-testid="staff-chat-input"
            />
            <button onClick={sendStaffMsg} disabled={staffSending || !staffInput.trim()} className="p-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 transition-colors" data-testid="staff-chat-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </>
      )}

      {/* ===== AI AIDE TAB ===== */}
      {activeTab === 'aide' && (
        <>
          <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
            {aiView === 'home' && (
              <div className="p-4 space-y-4">
                {tours.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-gray-500">
                      <Map className="w-3 h-3 inline mr-1" /> {t('chatWidget.interactiveGuides')}
                    </p>
                    <div className="space-y-2">
                      {tours.map(tour => (
                        <button key={tour.id} onClick={() => { setActiveTour(tour); setTourStep(0); setAiView('tour'); }}
                          className="w-full text-left p-3 rounded-xl border transition-all group hover:scale-[1.01]"
                          style={{ background: 'rgba(124,58,237,0.06)', borderColor: 'rgba(124,58,237,0.15)' }}>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-[13px] text-gray-200 font-medium">{tour.title}</span>
                              <p className="text-[10px] mt-0.5 text-gray-500">{tour.description}</p>
                            </div>
                            <span className="text-[9px] px-1.5 py-0.5 rounded-md font-bold" style={{ background: 'rgba(124,58,237,0.2)', color: '#a78bfa' }}>{t('chatWidget.stepsCount', { count: tour.steps.length })}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-gray-500">
                    <HelpCircle className="w-3 h-3 inline mr-1" /> {t('chatWidget.faqTitle')}
                  </p>
                  <div className="space-y-1.5">
                    {faq.map((item, i) => (
                      <button key={i} onClick={() => { setAiMessages(prev => [...prev, { role: 'user', content: item.q }, { role: 'assistant', content: item.a }]); setAiView('chat'); }}
                        className="w-full text-left p-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:border-purple-500/30 transition-all">
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-gray-300">{item.q}</span>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {aiView === 'chat' && (
              <div className="p-3 space-y-2">
                <button onClick={() => setAiView('home')} className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1 mb-1">
                  <ArrowLeft className="w-3 h-3" /> {t('chatWidget.back')}
                </button>
                {aiMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs ${
                      msg.role === 'user' ? 'bg-purple-600 text-white rounded-br-md' : 'bg-white/8 text-gray-200 border border-white/10 rounded-bl-md'
                    }`}>
                      <div className="break-words whitespace-pre-wrap">{msg.content}</div>
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-2xl bg-white/5 border border-white/10">
                      <div className="flex gap-1"><span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} /></div>
                    </div>
                  </div>
                )}
                <div ref={aiEndRef} />
              </div>
            )}

            {aiView === 'tour' && activeTour && (
              <div className="p-4">
                <button onClick={() => { setAiView('home'); setActiveTour(null); }} className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1 mb-3">
                  <ArrowLeft className="w-3 h-3" /> {t('chatWidget.back')}
                </button>
                <h4 className="text-sm font-bold text-white mb-1">{activeTour.title}</h4>
                <p className="text-[10px] text-gray-500 mb-3">{t('chatWidget.stepProgress', { current: tourStep + 1, total: activeTour.steps.length })}</p>
                <div className="bg-white/5 rounded-xl border border-white/10 p-4">
                  <p className="text-sm text-gray-200">{activeTour.steps[tourStep]?.text || ''}</p>
                </div>
                <div className="flex gap-2 mt-3">
                  {tourStep > 0 && <button onClick={() => setTourStep(s => s - 1)} className="flex-1 py-2 rounded-xl bg-white/10 text-white text-xs">{t('chatWidget.previous')}</button>}
                  <button onClick={() => { if (tourStep < activeTour.steps.length - 1) { setTourStep(s => s + 1); } else { setAiView('home'); setActiveTour(null); } }}
                    className="flex-1 py-2 rounded-xl bg-purple-600 text-white text-xs font-semibold">
                    {tourStep < activeTour.steps.length - 1 ? t('chatWidget.next') : <><CheckCircle2 className="w-3 h-3 inline mr-1" /> {t('chatWidget.finish')}</>}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* AI Input */}
          <div className="shrink-0 px-3 py-2 border-t border-white/10 flex items-center gap-2" style={{ background: '#0a0a1a' }}>
            <input
              type="text"
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendAiMessage(aiInput)}
              placeholder={t('chatWidget.aiInputPlaceholder')}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/50"
              data-testid="ai-chat-input"
            />
            <button onClick={() => sendAiMessage(aiInput)} disabled={aiLoading || !aiInput.trim()} className="p-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-40 transition-colors" data-testid="ai-chat-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientChatWidget;
